import { observer } from 'mobx-react';
import React, { isValidElement } from 'react';
import { useLocation } from 'react-router';

import useLoginDialog from 'hooks/use-login-dialog';
import useStores from 'hooks/use-stores';
import { isProtectedData, parseProtectedData } from 'pages/components/ProtectedData/utils';

import { PLACEHOLDER } from './constants';
import useStyles from './styles';
import { Primitive, Type } from './types';

type RenderFunction<Options> = (options: Options) => React.ReactNode;

type ChildrenRenderOptions<T> =
    | {
          content: string;
          size: number;
          type: Type;
          isProtected: true;
      }
    | {
          content: T;
          isProtected: false;
      };

export interface ProtectedDataProps<T extends Primitive = Primitive> {
    data: T;
    children?: React.ReactNode | RenderFunction<ChildrenRenderOptions<T>>;
    rounded?: boolean;
}

function ProtectedData<T extends Primitive>({ data, children, rounded }: ProtectedDataProps<T>) {
    const { classes, cx } = useStyles();
    const { userStore } = useStores();

    const location = useLocation();
    const [dialog, openDialog] = useLoginDialog(location.pathname + location.search);

    const isProtected = isProtectedData(data);
    const isRenderFunction = typeof children === 'function';

    if (isProtected) {
        const { type, size } = parseProtectedData(data);
        const content = PLACEHOLDER.repeat(size);

        return (
            <>
                <span
                    className={cx(classes.blur, { [classes.rounded]: rounded })}
                    onClick={() => userStore.user.isAnonymous && openDialog()}
                >
                    {isRenderFunction ? children({ isProtected: true, content, type, size }) : content}
                </span>
                {dialog}
            </>
        );
    }

    if (children === undefined) {
        if (data) {
            return isValidElement(data) ? data : String(data);
        } else {
            return null;
        }
    }

    if (isRenderFunction) {
        return children({ content: data, isProtected: false });
    }

    return children;
}

export default observer(ProtectedData);
